import {
  Building2Icon,
  CheckCircle2Icon,
  ClipboardListIcon,
  SearchIcon,
  SlidersHorizontalIcon,
  UsersRoundIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useCompaniesQuery } from '@/modules/companies/queries/useCompaniesQuery';
import { ScoutPromptInput } from '@/modules/scout-sourcing/components/ScoutPromptInput';
import { useNextTargetingRoundMutation } from '@/modules/scout-sourcing/mutations/useNextTargetingRoundMutation';
import { useStartScoutFromPromptMutation } from '@/modules/scout-sourcing/mutations/useStartScoutFromPromptMutation';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';
import { Combobox } from '@/modules/ui/components/combobox/Combobox';
import Container from '@/modules/ui/components/container/Container';
import { PageHeader } from '@/modules/ui/components/page-header/PageHeader';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { Subheading } from '@/modules/ui/components/subheading/Subheading';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const promptIngredients = [
  {
    icon: SearchIcon,
    title: 'Search target',
    description:
      'Role titles, seniority, location, remote policy, and the markets where the right people are likely to work.',
  },
  {
    icon: CheckCircle2Icon,
    title: 'Proof signals',
    description:
      'The profile evidence that matters: skills, tools, company stage, industry exposure, or measurable outcomes.',
  },
  {
    icon: SlidersHorizontalIcon,
    title: 'Tradeoffs',
    description:
      'What is flexible, what is non-negotiable, and which backgrounds Scout should avoid.',
  },
];

const promptExamples = [
  {
    label: 'Engineering',
    text: 'Senior frontend engineer in Prague or remote EU, strong React and TypeScript, 5+ years, ideally B2B SaaS or fintech. Prioritize product-minded builders who owned complex UI systems. Avoid candidates focused only on agency websites.',
  },
  {
    label: 'Commercial',
    text: 'Enterprise account executive in London with 4+ years selling HRtech or recruiting software to companies with 500+ employees. Need evidence of outbound pipeline creation and multi-stakeholder deals. Open to adjacent B2B SaaS if ACV is similar.',
  },
  {
    label: 'Leadership',
    text: 'Head of Talent in Berlin for a Series B climate-tech company. Look for leaders who scaled hiring from 50 to 200+ employees, built recruiter teams, and partnered closely with founders. Startup experience matters more than big-company brand.',
  },
];

const formatDomain = (url?: string | null) => {
  if (!url) {
    return null;
  }
  return url
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/.*$/, '');
};

export function ScoutSourcing() {
  const navigate = useNavigate();
  const companiesQuery = useCompaniesQuery();
  const startMutation = useStartScoutFromPromptMutation();
  const nextMutation = useNextTargetingRoundMutation();

  const [prompt, setPrompt] = useState('');
  const [companyId, setCompanyId] = useState<string | undefined>();

  const companyOptions = useMemo(
    () =>
      (companiesQuery.data ?? []).map((c) => ({
        value: c.id ?? '',
        label: c.name ?? '',
      })),
    [companiesQuery.data],
  );

  const effectiveCompanyId =
    companyId ??
    (companyOptions.length === 1 ? companyOptions[0]?.value : undefined);

  const selectedCompany = useMemo(
    () => (companiesQuery.data ?? []).find((c) => c.id === effectiveCompanyId),
    [companiesQuery.data, effectiveCompanyId],
  );
  const domain = formatDomain(selectedCompany?.websiteUrl);

  const onContinue = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a brief or paste a job description.');
      return;
    }
    if (companyOptions.length > 1 && !effectiveCompanyId) {
      toast.error('Choose a company first.');
      return;
    }
    try {
      const drafted = await startMutation.mutateAsync({
        prompt: prompt.trim(),
        companyId: effectiveCompanyId,
      });
      const firstRound = await nextMutation.mutateAsync({
        jobId: drafted.jobId,
        jobSourcingId: drafted.jobSourcingId,
        votesForPreviousRound: null,
      });
      toast.success('Scout brief created');
      navigate(`/jobs/${drafted.jobId}/scout`, {
        state: {
          jobSourcingId: drafted.jobSourcingId,
          firstRound,
        },
      });
    } catch (e) {
      toast.error(`Failed to start Scout: ${extractErrorMessage(e)}`);
    }
  };

  const isPending = startMutation.isPending || nextMutation.isPending;
  const onUseStarter = (text: string) => {
    setPrompt(text);
  };

  return (
    <Container>
      <PageHeader
        title="Brief Scout"
        subtitle="Describe who to find, what proof signals matter, and where Scout can flex."
        action={
          selectedCompany && (
            <Button asChild variant="outline" size="sm">
              <Link to={`/companies/${selectedCompany.id}/edit`}>
                Manage company
              </Link>
            </Button>
          )
        }
      />

      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 pb-8">
        <Card>
          <CardHeader className="gap-4 md:flex md:flex-row md:items-center md:justify-between">
            <div className="space-y-1.5">
              <CardTitle>Search brief</CardTitle>
              <CardDescription>
                Start with a job description or write the sourcing strategy
                directly.
              </CardDescription>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="secondary">Live criteria</Badge>

              {companyOptions.length > 1 ? (
                <div className="flex items-center gap-2">
                  <Building2Icon className="text-muted-foreground size-4" />
                  <Combobox
                    value={companyId ?? ''}
                    placeholder="Choose company..."
                    searchPlaceholder="Search company..."
                    emptyMessage="No company found."
                    options={companyOptions}
                    onChange={(v) => setCompanyId(v || undefined)}
                  />
                </div>
              ) : (
                <div className="text-muted-foreground flex items-center gap-2 text-sm">
                  <Building2Icon className="size-4" />
                  <span>{selectedCompany?.name ?? 'Your company'}</span>
                  {domain && (
                    <span className="hidden sm:inline">· {domain}</span>
                  )}
                </div>
              )}
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            <ScoutPromptInput
              value={prompt}
              onChange={setPrompt}
              onSubmit={onContinue}
              submitLabel="Create"
              disabled={companyOptions.length > 1 && !effectiveCompanyId}
              loading={isPending}
              rows={5}
              className="bg-background rounded-lg border p-4"
              textareaClassName="min-h-40 text-base"
              placeholder="e.g. Senior frontend engineer in Prague or remote EU, strong React and TypeScript, 5+ years, fintech or B2B SaaS. Prioritize product-minded builders who owned complex UI systems. Avoid agency-only profiles."
            />

            <Paragraph className="text-muted-foreground text-sm">
              Press Enter or click Create. The criteria update as Scout
              understands the brief.
            </Paragraph>
          </CardContent>
        </Card>

        <section className="grid gap-6 lg:grid-cols-[1fr_0.8fr]">
          <Card>
            <CardHeader>
              <CardTitle>Strong Scout prompts</CardTitle>
              <CardDescription>
                A job description is the role. A Scout brief is the strategy.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {promptIngredients.map((ingredient) => {
                  const Icon = ingredient.icon;

                  return (
                    <div
                      key={ingredient.title}
                      className="bg-background rounded-md border p-4"
                    >
                      <div className="flex items-start gap-3">
                        <div className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-md">
                          <Icon className="size-4" />
                        </div>
                        <div>
                          <Subheading variant="h6">
                            {ingredient.title}
                          </Subheading>
                          <Paragraph className="text-muted-foreground mt-1">
                            {ingredient.description}
                          </Paragraph>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div>
                <CardTitle>Prompt starters</CardTitle>
                <CardDescription>
                  Use one, then make it specific.
                </CardDescription>
              </div>
              <CardAction>
                <div className="bg-muted flex size-9 items-center justify-center rounded-md">
                  <ClipboardListIcon className="text-muted-foreground size-4" />
                </div>
              </CardAction>
            </CardHeader>

            <CardContent>
              <div className="flex flex-col gap-3">
                {promptExamples.map((example) => (
                  <button
                    key={example.label}
                    type="button"
                    onClick={() => onUseStarter(example.text)}
                    className="group hover:border-primary hover:bg-accent bg-background rounded-md border px-4 py-3 text-left transition-colors"
                  >
                    <span className="mb-2 flex items-center justify-between gap-3">
                      <span className="text-foreground text-sm font-medium">
                        {example.label}
                      </span>
                      <UsersRoundIcon className="text-muted-foreground group-hover:text-primary size-4 transition-colors" />
                    </span>
                    <span className="text-muted-foreground line-clamp-3 text-sm leading-6">
                      {example.text}
                    </span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </Container>
  );
}
